import * as React from "react";
import { SplitButton } from "@bizform/primereact/components/splitbutton/SplitButton";
import { SplitButtonProps } from "@bizform/primereact/splitbutton";
import { Button } from "@bizform/primereact/button";
import { MenuItem } from "@bizform/primereact/components/menuitem/MenuItem";
import TooltipOptions from "@bizform/primereact/components/tooltip/TooltipOptions";
import { Static } from "@bizform/type/decorator/Static.decorator";
import {
    ComponentElement,
    ComponentElementDisplayMode,
    ComponentElementStatic
} from "@bizform/type/model/component_element/ComponentElement.model";
import {
    ComponentCategory,
    ComponentElementManifest,
    ComponentElementProperty
} from "@bizform/type/model/component_element/ComponentElementManifest.model";
import { ComponentHelper, Debug, DomHelper, ObjectHelper } from "@bizform/helper/dist/js/bizflow/helper.min";
import { ComponentElementClass, ReactComponentElementInit } from "@bizform/core/dist/js/bizflow/core.min";
import { I18n } from "../../misc/model/I18n.model";
import { AbstractComponentElement } from "../AbstractComponentElement";


type Props = SplitButtonProps & {};

type WrappedCompState = {
    label?: string;
    icon?: string;
    tabindex?: number;
    model: MenuItem[];
    disabled?: boolean;
    style?: object;
    className?: string;
    menuStyle?: object;
    menuClassName?: string;
    // tabIndex?: string;
    // appendTo?: HTMLElement;
    // tooltip?: any;
    // tooltipOptions?: TooltipOptions;
    tooltipAttribute?: TooltipOptions & {text?: string};

    // event
    onClick?(event: Event): void;
};

type WrappedFormState = {
    onClick?: string;
};

type State = {
    show: boolean;
    displayMode: number;
    wrappedCompState: WrappedCompState;
    wrappedFormState: WrappedFormState;
};

/**
 * @since 0.0.1
 * @author Byungmin Choi, Hwansoon Park
 * @class
 * @description
 * SplitButton component element
 */
@ComponentElementClass()
@Static<ComponentElementStatic<null>>()
export class BfSplitButton extends AbstractComponentElement<Props, State> {
    /* manifest */
    /*
     * property panel 에서 이 manifest 를 기준으로 속성 및 이벤트를 설정할 때
     * SplitButton 컴포넌트의 model 프로퍼티는 그 type 이 FORMSTATE 이므로
     * 메뉴바의 값 변경 시 emitValue 가 아닌 emitFormData 를 수행한다.
     * 또한, command 는 외부에서 재정의할 수 없으며(property panel에 노출시키지 않음)
     * property panel 에서 설정한 Form State 변경만 가능하게 사용자 인터페이스를 제공해야 한다.
     * 단, 각각의 서브 메뉴가 built-in command 를 호출하기 위해서
     * 적절한 별도의 property 정의가 필요하다.
     */
    static readonly componentElementManifest: ComponentElementManifest = {
        uniqueId: "_P4Ru8RTTkroOveAtDxw5KBaDl94foiv",
        category: ComponentCategory.BUTTON,
        label: I18n.componentElement.splitButton.label,
        description: I18n.componentElement.splitButton.description,
        icon: "",
        property: null
    };

    focus(): void {
        if (this._splitButton) {
            this._splitButton.focus();
        }
    }

    closeOverlay(): void {
    }

    setProperty(property: ComponentElementProperty): void {
        try {
            const newState = ComponentHelper.createStateWithProperties<State, null>(
                property,
                this);
            this.setState(newState);
        } catch (e) {
            Debug.console.error("setProperty", e);
        }
    }

    private _itemValueEvent(e: { originalEvent: Event, item: MenuItem }): void {
        /*
         * 주의:
         * itemValue 는 MenuItem 에 정의된 값이 프로퍼티가 아니다.
         * property panel 에서 form state 변경을 위해 itemValue 를 컴포넌트에 설정하고
         * command 함수(메뉴 클릭)가 호출될 때 이 itemValue 를 전달하기 위해
         * MenuItem 객체에 강제로 주입된 속성이다.
         */
        const itemValue = e.item && (e.item as { itemValue }).itemValue;
        if (itemValue) {
            this.emitFormState(itemValue);
        }
    }

    emitFormState(value: string): void {
    }

    private _onClickEvent(event): void {
    }

    @ReactComponentElementInit(BfSplitButton)
    static init(): ComponentElement<null> {
        return;
    }

    initDone(): void {
    }

    destroy(): void {
        // proceed something to clean up
        this.destroyDone();
    }

    destroyDone(): void {
    }

    /* for React */
    props: Props;
    state: State;

    static defaultProps = {};

    private _splitButton: HTMLDivElement;

    constructor(props?: Props) {
        super(props);

        this.state = {
            show: false,
            displayMode: ComponentElementDisplayMode.INIT,
            wrappedCompState: {
                model: null
            },
            wrappedFormState: {
                onClick: null
            }
        };

        this._subButton = this._subButton.bind(this);
        this._subButtonEvent = this._subButtonEvent.bind(this);
        this._setRef = this._setRef.bind(this);
    }

    shouldComponentUpdate(nextProps: Readonly<Props>, nextState: Readonly<State>, nextContext: any): boolean {
        return ObjectHelper.hasDifference(nextState, this.state);
    }

    componentDidUpdate(prevProps: Readonly<SplitButtonProps & {}>, prevState: Readonly<State>, snapshot?: any): void {
        if (this._splitButton) {
            this._splitButton.getElementsByTagName("button")[0].setAttribute("class", "p-button p-component p-button-text-only bf-split-btn");
            if (this.state.wrappedCompState.tabindex !== this.___tabindex) {
                ComponentHelper.tabindex(this.displayMode, Array.from(this._splitButton.querySelectorAll("button")), this.state.wrappedCompState.tabindex);
                this.___tabindex = this.state.wrappedCompState.tabindex;
            }
        }
    }

    componentWillUnmount(): void {
        this._splitButton = null;
    }

    componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
        Debug.console.error(error, errorInfo);
    }

    render(): React.ReactNode {
        const {
            show,
            displayMode
        } = this.state;

        if (!show) {
            return null;
        } else {
            /*
             * SplitButton doesn't have readOnly, disabled properties.
             */
            switch (displayMode) {
                case ComponentElementDisplayMode.EDIT:
                case ComponentElementDisplayMode.PRINT:
                    return this._render4Edit();
                case ComponentElementDisplayMode.READONLY:
                case ComponentElementDisplayMode.DISABLED:
                    return this._render4Disabled();
                default:
                    return null;
            }
        }
    }

    private _render(newProps: any): React.ReactNode {
        const key = ComponentHelper.tooltipProps(newProps, this.elementId, this.accessibility);
        if (this.accessibility) {
            return (
                <div ref={this._setRef} className="w-100 h-100 p-0 m-0">
                    <Button
                        {...newProps}
                        id={this.elementId}
                        key={key}
                        style={{ width: "100%"}}
                    />
                    {Array.isArray(newProps.model) ? newProps.model.map(this._subButton) : null}
                </div>
            );
        } else {
            return (
                <SplitButton
                    {...newProps}
                    key={key}
                    id={this.elementId}
                    ref={this._setRef}
                    style={{ width: "100%", ...newProps.style }}
                    appendTo={DomHelper.getForOverLayDom()}
                />
            );
        }
    }

    private _render4Edit(): React.ReactNode {
        const newProps = {
            ...this.state.wrappedCompState,
        };

        return this._render(newProps);
    }

    private _render4Disabled(): React.ReactNode {
        const newProps = {
            ...this.state.wrappedCompState,
            disabled: true
        };

        return this._render(newProps);
    }

    private _subButton(item: MenuItem, index: number): React.ReactNode {
        return <Button
            bf-data-value={index}
            key={this.elementId + "_" + index}
            className="mt-1"
            style={{ width: "100%"}}
            label={item.label}
            onClick={this._subButtonEvent}
        />;
    }

    private _subButtonEvent(e: any) {
        const index = e.target.getAttribute("bf-data-value") || e.target.parentElement.getAttribute("bf-data-value");
        this._itemValueEvent({originalEvent: e, item: this.state.wrappedCompState.model[index]});
    }

    private _setRef(ref): void {
        if (ref) {
            this._splitButton = this.accessibility ? ref : ref.container;
            this.readyComponent();
        }
    }
}
